import { Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ManifoldNextGenAccountMenu } from "~/components/ManifoldNextGenAccountMenu";
import { ManifoldNextGenNavPicker } from "~/components/ManifoldNextGenNavPicker";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import useNextGenHeader from "~/contexts/nextgen-header";
import { cn } from "~/lib/utils";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";

interface ManifoldNextGenAppHeaderProps {
  onSwitchToClassic?: () => void;
  canGoBack?: boolean;
}

export function ManifoldNextGenAppHeader({
  onSwitchToClassic,
  canGoBack = true,
}: ManifoldNextGenAppHeaderProps) {
  const { breadcrumbs, title, actions, rightPane, variant, heroContent, onBack } =
    useNextGenHeader();
  const navigate = useNavigate();
  const colored = variant === "colored";
  const handleBack = canGoBack ? onBack : () => navigate(ROUTE_NEXTGEN_VIEW);

  // nav-picker (auto) | breadcrumbs (1fr) | title (auto) | actions+avatar (auto) | optional rightPane
  const gridCols = rightPane
    ? "grid-cols-[auto_1fr_auto_auto_auto]"
    : "grid-cols-[auto_1fr_auto_auto]";

  return (
    <div
      className={cn(
        "relative transition-colors duration-300",
        colored ? "bg-[var(--dt-primitives-color-brand-deep-space)]" : "bg-transparent"
      )}
    >
      {colored && (
        // Matches the source design's exact glow geometry. Deliberately not
        // clipped with `overflow-hidden` on the wrapper — that would also
        // clip the search dropdown rendered inside `heroContent`, which must
        // be free to extend below this box.
        <div
          aria-hidden="true"
          className="pointer-events-none absolute size-[340px] rounded-full"
          style={{
            top: "-120px",
            right: "-60px",
            background: "radial-gradient(circle, rgba(151,71,255,.45), transparent 70%)",
          }}
        />
      )}
      <div
        data-testid="nextgen-app-header"
        className={cn(
          "grid shrink-0 items-stretch border-b",
          gridCols,
          colored ? "border-transparent bg-transparent" : "border-border bg-sidebar"
        )}
      >
        <div className="flex items-center pl-3">
          <ManifoldNextGenNavPicker colored={colored} />
        </div>

        <div className="flex items-center py-2 pl-2">
          {onBack && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              aria-label="Back"
              className="mr-1 size-8 shrink-0"
            >
              <ArrowLeft className="size-4" />
            </Button>
          )}
          {breadcrumbs.length > 0 ? (
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1;
                  return (
                    <Fragment key={index}>
                      <BreadcrumbItem>
                        {isLast || !crumb.href ? (
                          <BreadcrumbPage className="text-sm font-medium text-foreground">
                            {crumb.label}
                          </BreadcrumbPage>
                        ) : (
                          /* Styled here rather than on the `Link`: `asChild`
                             renders a Radix `Slot`, which string-joins the two
                             classNames instead of running them through `cn`, so
                             a utility set on the child only collides with the
                             primitive's. */
                          <BreadcrumbLink
                            asChild
                            className="font-medium text-foreground opacity-70 hover:opacity-100"
                          >
                            <Link to={crumb.href}>{crumb.label}</Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                      {!isLast && <BreadcrumbSeparator />}
                    </Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          ) : (
            title && <span className="px-2 text-sm font-medium text-foreground">{title}</span>
          )}
        </div>

        <div className="flex items-center justify-center px-4 py-2">
          {breadcrumbs.length > 0 && title && (
            <span className="text-sm font-medium text-foreground">{title}</span>
          )}
        </div>

        <div className="flex items-center gap-2 py-2 pr-4">
          {actions[0]}
          {actions[1]}
          <ManifoldNextGenAccountMenu onSwitchToClassic={onSwitchToClassic} colored={colored} />
        </div>
        {rightPane}
      </div>
      {colored && heroContent}
    </div>
  );
}
